import {
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Theme, styled, useTheme } from "@mui/material/styles";
import safeStringify from "fast-safe-stringify";

import { GetStaticProps, NextPage } from "next";
import Image from "next/image";

import React, { useContext, useEffect, useState } from "react";

import { ContentfulNavigation } from "../../@types/contentfulNavigation";
import { ShopifyProductWithVariant } from "../../@types/shopifyProductWithVariant";
import CartAmountToggle from "../../components/cart/amountToggle";
import { CartDrawerItem } from "../../components/cart/cart-nav-item";
import CustomContainer from "../../components/container";
import PageLayout from "../../components/page/layout";
import {
  CartLineItemWithProduct,
  getCartItems,
  getCartSumTotal,
  getCartSumTotalWithoutDiscounts,
  getPriceComponents,
  isCartEmpty,
} from "../../utils/cart/cart";
import { CartContext, CartContextType } from "../../utils/contexts";
import ContentfulNavigationRequest from "../../utils/graphQL/contentful/ContentfulNavigationRequest";
import { formatPrice } from "../../utils/helperFunctions";
import generateTitle from "../../utils/page/generateTitle";
import getJointPageProperties, {
  JointPageProperties,
} from "../../utils/page/getJointPageProperties";
import { createCheckoutApiURL } from "../../utils/shopify/createCart";
import { DOC_FLECK_TENANT_NAME } from "../../utils/tenant-names";
import { pushDataLayerBeginCheckout } from "../../utils/trackingEvents/begin-checkout";

interface CartPageProps {
  isPreview?: boolean;
  previewData?: any;
  navigation: ContentfulNavigation;
  jointPageProperties: JointPageProperties;
}

/**
 * course page
 * @returns JSX.Element
 */
const CartPage: NextPage<CartPageProps> = ({
  isPreview,
  previewData,
  navigation,
  jointPageProperties,
}) => {
  const { footerProps } = jointPageProperties;
  const {
    logoDark,
    logoLight,
    titleSuffix,
    bannerVisible,
    bannerText,
    bannerIcon,
  } = jointPageProperties.siteSettings;
  const seoMetadata = {
    fields: {
      internal: "string",
      title: "Warenkorb",
      description: "Dein Warenkorb",
      keywords: undefined,
      noIndex: true,
      noFollow: true,
      ogMetadata: undefined,
      twitterMetadata: undefined,
    },
  };
  const fullTitle = generateTitle("Warenkorb", titleSuffix);

  const theme = useTheme();

  const cartContext = useContext(CartContext);
  const { cart } = cartContext;

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [order, setOrder] = useState(Object.keys(cart.items));

  var cartTotalSum = getCartSumTotal(cart);
  var { mwst, basePrice } = getPriceComponents(cartTotalSum);

  useEffect(() => {
    cartTotalSum = getCartSumTotal(cart);
    let priceComponents = getPriceComponents(cartTotalSum);
    mwst = priceComponents.mwst;
    basePrice = priceComponents.basePrice;
    const newOrder = order
      .slice()
      .filter((key) => cart.items.hasOwnProperty(key));
    Object.keys(cart.items)
      .filter((key) => newOrder.indexOf(key) == -1)
      .forEach((key) => newOrder.push(key));
    setOrder(newOrder);
  }, [cart]);

  const itemsSorted = Object.keys(cart.items).sort(
    (keyA, keyB) => order.indexOf(keyA) - order.indexOf(keyB)
  );

  const totalCartAmount = getCartSumTotal(cart);
  const totalCartAmountWithoutDiscount = getCartSumTotalWithoutDiscounts(cart);

  return (
    <PageLayout
      title={fullTitle}
      footer={footerProps}
      logoHeader={logoDark}
      logoFooter={logoLight}
      navigation={navigation}
      bannerVisible={bannerVisible}
      bannerText={bannerText}
      bannerIcon={bannerIcon}
      metadata={seoMetadata.fields}
      tenant={DOC_FLECK_TENANT_NAME}
    >
      <CustomContainer>
        <Container>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "left",
              marginBottom: theme.spacing(3),
            }}
          >
            <Typography variant="h2" sx={{ mb: theme.spacing(3) }}>
              Dein Warenkorb
            </Typography>
            {isSmallScreen ? (
              <Box>
                {itemsSorted.map((variantId, i) => {
                  const cartItem = cart.items[variantId];

                  return (
                    <CartDrawerItem
                      key={`cartItem${i}`}
                      cartItem={cartItem}
                      theme={theme}
                      cartContext={cartContext}
                    />
                  );
                })}
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: theme.spacing(2),
                    mb: theme.spacing(2),
                    borderBottom: `0px solid ${theme.palette.softBlack.light}`,
                    borderTop: `2px solid ${theme.palette.softBlack.light}`,
                  }}
                >
                  {cart.isUpdatingPrices && (
                    <LinearProgress
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                      }}
                    />
                  )}
                  {totalCartAmountWithoutDiscount > totalCartAmount && (
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body1">Rabatt</Typography>
                      {!cart.isUpdatingPrices && (
                        <Typography variant="body1">
                          -
                          {formatPrice(
                            totalCartAmountWithoutDiscount - totalCartAmount,
                            "EUR"
                          )}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body1">Zwischensumme</Typography>
                    {!cart.isUpdatingPrices && (
                      <Typography variant="body1">
                        {formatPrice(cartTotalSum, "EUR")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ tableLayout: "fixed" }}>
                <Table
                  sx={{ minWidth: 650, border: "none" }}
                  aria-label="simple table"
                >
                  <TableHead
                    sx={{
                      backgroundColor: theme.components?.Banner.backgroundColor,
                    }}
                  >
                    <TableRow>
                      <TableCell component="th" scope="row"></TableCell>

                      <TableCell
                        sx={{ fontWeight: theme.typography.h4.fontWeight }}
                        align="left"
                      >
                        Artikel
                      </TableCell>

                      <TableCell
                        sx={{ fontWeight: theme.typography.h4.fontWeight }}
                        align="center"
                      >
                        Preis
                      </TableCell>

                      <TableCell
                        sx={{ fontWeight: theme.typography.h4.fontWeight }}
                        align="center"
                      >
                        Menge
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: theme.typography.h4.fontWeight }}
                        align="right"
                      >
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  {isCartEmpty(cart) && Object.keys(cart.items).length == 0 ? (
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          Du hast keine Waren in ihrem Warenkorb.
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {cart &&
                        itemsSorted.map((variantId, i) => {
                          const cartItem = cart.items[variantId];
                          return (
                            <CartItemTableRow
                              key={`cartItemRow${i}`}
                              cartItem={cartItem}
                              cartContext={cartContext}
                              theme={theme}
                            />
                          );
                        })}

                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            borderTop: `2px solid ${theme.palette.secondary.main}`,
                            borderBottom: `none`,
                          }}
                        >
                          <Typography variant="body2">Zwischensumme</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderTop: `2px solid ${theme.palette.secondary.main}`,
                            borderBottom: `none`,
                          }}
                          align="right"
                        >
                          <Typography variant="body2">
                            {formatPrice(basePrice)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            borderTop: `none`,
                          }}
                          colSpan={4}
                        >
                          <Typography variant="body2">
                            zzgl. 19% MwSt.
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderTop: `none`,
                          }}
                          align="right"
                        >
                          <Typography variant="body2">
                            {formatPrice(mwst)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow
                        sx={{
                          color: theme.palette.primary.main,
                        }}
                      >
                        <TableCell
                          colSpan={4}
                          sx={{
                            borderTop: `2px solid ${theme.palette.secondary.main}`,
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{ color: theme.palette.softBlack.main }}
                          >
                            Gesamtsumme
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderTop: `2px solid ${theme.palette.secondary.main}`,
                          }}
                          align="right"
                        >
                          <Typography
                            variant="h5"
                            sx={{ color: theme.palette.softBlack.main }}
                          >
                            {formatPrice(getCartSumTotal(cart))}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            )}
          </Box>
        </Container>
        <Container>
          <Box
            sx={{
              mb: theme.spacing(4),
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              disabled={isCartEmpty(cart)}
              variant="contained"
              href={
                cart.checkoutURL ||
                createCheckoutApiURL(
                  Object.keys(cart.items).flatMap((variantId) =>
                    Array(cart.items[variantId].amount).fill(variantId)
                  )
                )
              }
              onClick={() => writeTrackingEvents(getCartItems(cart))}
            >
              Jetzt Kaufen
            </Button>
          </Box>
        </Container>
      </CustomContainer>
    </PageLayout>
  );
};

export default CartPage;

/**
 * This function gets called at build time on server-side to statically pre-fetch the data for the course page
 * @param {GetStaticPropsContext<ParsedUrlQuery, PreviewData>} context
 * @returns course page data
 */
export const getStaticProps: GetStaticProps<CartPageProps> = async (
  context
) => {
  const [jointPageProperties] = await Promise.all([
    getJointPageProperties(context, DOC_FLECK_TENANT_NAME),
  ]);

  const pageProps = safeStringify({
    isPreview: context.preview || false,
    previewData: context.previewData || {
      env: process.env.NEXT_PUBLIC_CONTENTFUL_ENV,
      internal: "",
    },
    navigation: jointPageProperties.navigation,
    jointPageProperties,
    theme: DOC_FLECK_TENANT_NAME,
  });

  const props = JSON.parse(pageProps);
  // Pass  page data to the page via props
  return {
    props,
  };
};

const CartItemTableRow = (props: {
  cartItem: CartLineItemWithProduct;
  cartContext: CartContextType;
  theme: Theme;
}) => {
  const { cartItem, cartContext, theme } = props;
  const { setCartItemAmount, cart } = cartContext;
  const { product, amount } = cartItem;

  const isDeleted = cartItem.amount <= 0;

  const StyledCell = styled(TableCell)(({ theme }) => {
    return {
      transition: "0.4s all ease-out",
      opacity: isDeleted ? "0" : "1",
    };
  });

  return isDeleted ? (
    <></>
  ) : (
    <>
      <TableRow
        sx={{
          position: "relative",
          transition: "0.4s background-color ease-out",
          backgroundColor: isDeleted
            ? theme.palette.secondary.main
            : theme.palette.pureWhite.main,
        }}
      >
        <TableCell component="th" scope="row">
          <Box
            sx={{
              height: "100px",
              width: "100px",
              position: "relative",
              maxHeight: isDeleted ? "0px" : "100px",
              filter: isDeleted ? "grayScale(1)" : "grayScale(0)",
              transition: "0.5s max-height ease-in",
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor: "rgba(0,0,0,0.1)",
              border: "1px solid " + theme.palette.softBlack.light,
            }}
          >
            {product.media && (
              <Image
                style={{ objectFit: "contain" }}
                fill
                alt={product.media?.alt || ""}
                src={product.media?.previewUrl}
              ></Image>
            )}
          </Box>
        </TableCell>

        <TableCell align="left">
          <Typography
            sx={{
              transition: "0.4s color ease-out",
              color: isDeleted
                ? theme.palette.primary.main
                : theme.palette.softBlack.main,
            }}
          >
            {isDeleted ? <s>{product.title}</s> : product.title}
          </Typography>
        </TableCell>

        <StyledCell align="center">
          <Typography>{formatPrice(product.price.amount)}</Typography>
        </StyledCell>

        <TableCell align="center">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              transition: "0.4s all ease-out",
              opacity: isDeleted ? "0" : "1",
            }}
          >
            <CartAmountToggle
              setCartAmount={(amount) => {
                setCartItemAmount(cartItem, amount);
              }}
              amount={amount}
            />
          </Box>
          {/* <Button
            sx={{
              position: "absolute",
              top: "50%",
              right: 0,
              transform: "translate(-50%,-50%)",
              opacity: isDeleted ? 1 : 0,
              pointerEvents: isDeleted ? "all" : "none",
            }}
            variant="outlined"
            color="primary"
            onClick={() => {
              setCartItemAmount(cartItem, 1);
            }}
          >
            Wiederherstellen
          </Button> */}
        </TableCell>
        <StyledCell sx={{ width: "150px" }} align="right">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            {cartItem.cost.subtotalAmount > cartItem.cost.totalAmount && (
              <Typography
                variant="body1"
                sx={{
                  color: cart.isUpdatingPrices
                    ? theme.palette.softBlack.light
                    : theme.palette.softBlack.main,
                  fontWeight: "normal",
                  textAlign: "right",
                  // mr: theme.spacing(2),
                }}
              >
                <s>{formatPrice(cartItem.cost.subtotalAmount, "EUR")}</s>
              </Typography>
            )}
            <Typography
              variant="body1"
              sx={{
                color: cart.isUpdatingPrices
                  ? theme.palette.softBlack.light
                  : theme.palette.softBlack.main,
                textAlign: "right",
              }}
            >
              {formatPrice(cartItem.cost.totalAmount, "EUR")}
            </Typography>
          </Box>
        </StyledCell>
      </TableRow>
    </>
  );
};
function writeTrackingEvents(productList: ShopifyProductWithVariant[]): void {
  pushDataLayerBeginCheckout(productList);
}
